/**
 * Phase 12 — Admin V5 Course Bookings panel.
 *
 * A parallel, additive scheduling surface built on top of the existing
 * booking-series + classroom infrastructure. Nothing existing is modified.
 *
 * Features
 * --------
 *  - Searchable student + tutor dropdowns (with email auto-fill).
 *  - Weekday checkboxes + date range + planned session count.
 *  - Preview generated sessions before creating.
 *  - One reusable student + tutor classroom link per course (copyable).
 *  - Expandable course card with per-session attendance / earning controls.
 *  - Projected + confirmed tutor-earning totals updated live.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, RefreshCcw, Plus, Copy, ChevronDown, ChevronRight,
  Search, X, Check, Mail, GraduationCap, User, FileText, Edit,
  DollarSign, Calendar, Users, Link as LinkIcon, Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const WEEKDAYS = [
  { key: 'Mon', label: 'Mon', idx: 0 },
  { key: 'Tue', label: 'Tue', idx: 1 },
  { key: 'Wed', label: 'Wed', idx: 2 },
  { key: 'Thu', label: 'Thu', idx: 3 },
  { key: 'Fri', label: 'Fri', idx: 4 },
  { key: 'Sat', label: 'Sat', idx: 5 },
  { key: 'Sun', label: 'Sun', idx: 6 },
];

const AdminCourseBookingsPanel = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [expandedSessions, setExpandedSessions] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get('/api/admin/course-bookings');
      setCourses(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load course bookings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleExpand = async (courseId) => {
    if (expandedCourseId === courseId) {
      setExpandedCourseId(null); setExpandedSessions([]); return;
    }
    setExpandedCourseId(courseId);
    setExpandedLoading(true);
    try {
      const r = await ax.get(`/api/admin/course-bookings/${courseId}`);
      setExpandedSessions(r.data?.sessions || []);
      setCourses((prev) => prev.map((c) => (c.course_id === courseId ? { ...c, ...(r.data?.course || {}) } : c)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load sessions');
    } finally { setExpandedLoading(false); }
  };

  const patchSession = async (courseId, sessionId, patch) => {
    try {
      const r = await ax.patch(`/api/admin/course-bookings/${courseId}/sessions/${sessionId}`, patch);
      setExpandedSessions((prev) => prev.map((s) => (s.session_id === sessionId ? r.data.session : s)));
      // Refresh course stats so summary card updates.
      const cr = await ax.get(`/api/admin/course-bookings/${courseId}`);
      setCourses((prev) => prev.map((c) => (c.course_id === courseId ? { ...c, ...(cr.data?.course || {}) } : c)));
      toast.success('Session updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const copyLink = (url, label) => {
    if (!url) { toast.error(`No ${label} link`); return; }
    const full = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    navigator.clipboard?.writeText(full).then(
      () => toast.success(`${label} link copied`),
      () => toast.error('Could not copy — long-press to copy manually'),
    );
  };

  const cancelCourse = async (courseId) => {
    if (!window.confirm('Cancel this course? Scheduled sessions will be cancelled. Completed sessions and their earnings are preserved.')) return;
    try {
      const r = await ax.delete(`/api/admin/course-bookings/${courseId}`);
      toast.success(`Course cancelled (${r.data?.sessions_cancelled || 0} sessions cancelled)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
  };

  const [paymentModalCourse, setPaymentModalCourse] = useState(null);
  const [reviewModalCourse, setReviewModalCourse] = useState(null);
  const [editModalCourse, setEditModalCourse] = useState(null);
  const [manageTutorsCourse, setManageTutorsCourse] = useState(null);

  const sendInvites = async (courseId, targetOnly) => {
    try {
      const params = targetOnly ? { target: targetOnly } : {};
      const r = await ax.post(`/api/admin/course-bookings/${courseId}/send-invites`, null, { params });
      const outs = r.data?.outcomes || {};
      const parts = [];
      if (outs.student) parts.push(`student: ${outs.student.sent ? '✓ sent' : `✗ ${outs.student.reason || 'failed'}`}`);
      if (outs.tutor) parts.push(`tutor: ${outs.tutor.sent ? '✓ sent' : `✗ ${outs.tutor.reason || 'failed'}`}`);
      if (r.data?.ok) {
        toast.success(`Invites sent — ${parts.join(', ')}`);
      } else if (r.data?.sent?.length) {
        toast.warning(`Partial send — ${parts.join(', ')}`);
      } else {
        toast.error(`Invites failed — ${parts.join(', ')}`);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.health) {
        toast.error(`Cannot send — ${d.health.issues.join('; ')}`);
      } else {
        toast.error(typeof d === 'string' ? d : 'Send failed');
      }
    }
  };

  return (
    <Card data-testid="admin-course-bookings-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Course Bookings</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="course-refresh-btn">
            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="bg-red-700 hover:bg-red-800 text-white" data-testid="course-create-btn">
            <Plus className="h-4 w-4 mr-1" /> New course booking
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <p className="text-xs text-gray-500 -mt-2">
          Multi-session courses with a single reusable classroom link. Attendance and tutor earnings are tracked per session.
          The existing weekly Booking Series flow is unchanged and lives in the “Bookings &amp; Series” tab.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading courses…
          </div>
        ) : courses.length === 0 ? (
          <div className="text-sm italic text-gray-400 py-6 text-center border border-dashed border-gray-200 rounded-lg">
            No course bookings yet. Click <strong>New course booking</strong> to schedule your first course.
          </div>
        ) : (
          <div className="space-y-3">
            {courses.map((c) => (
              <CourseCard
                key={c.course_id}
                course={c}
                expanded={expandedCourseId === c.course_id}
                sessions={expandedCourseId === c.course_id ? expandedSessions : []}
                loadingSessions={expandedCourseId === c.course_id && expandedLoading}
                onToggle={() => toggleExpand(c.course_id)}
                onCopyLink={copyLink}
                onCancel={() => cancelCourse(c.course_id)}
                onSendInvites={(target) => sendInvites(c.course_id, target)}
                onReviewInvites={() => setReviewModalCourse(c)}
                onEditCourse={() => setEditModalCourse(c)}
                onManageTutors={() => setManageTutorsCourse(c)}
                onGeneratePayment={() => setPaymentModalCourse(c)}
                onPatchSession={(sid, patch) => patchSession(c.course_id, sid, patch)}
              />
            ))}
          </div>
        )}
      </CardContent>

      {createOpen && (
        <CreateCourseModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); fetchAll(); }}
        />
      )}
      {paymentModalCourse && (
        <GeneratePaymentLinkModal
          course={paymentModalCourse}
          onClose={() => setPaymentModalCourse(null)}
          onGenerated={() => setPaymentModalCourse(null)}
        />
      )}
      {reviewModalCourse && (
        <ReviewInvitesModal
          course={reviewModalCourse}
          onClose={() => setReviewModalCourse(null)}
          onSent={() => setReviewModalCourse(null)}
        />
      )}
      {editModalCourse && (
        <EditCourseModal
          course={editModalCourse}
          onClose={() => setEditModalCourse(null)}
          onSaved={() => { setEditModalCourse(null); fetchAll(); }}
        />
      )}
      {manageTutorsCourse && (
        <ManageTutorsModal
          course={manageTutorsCourse}
          onClose={() => setManageTutorsCourse(null)}
          onChanged={() => { fetchAll(); }}
        />
      )}
    </Card>
  );
};

// ============================================================================
// CourseCard — a single course row with expand-to-sessions
// ============================================================================

const CourseCard = ({ course, expanded, sessions, loadingSessions, onToggle, onCopyLink, onCancel, onSendInvites, onReviewInvites, onEditCourse, onManageTutors, onPatchSession, onGeneratePayment }) => {
  const curr = course.tutor_hourly_rate_currency || 'USD';
  const projected = course.projected_tutor_earnings_total ?? 0;
  const confirmed = course.confirmed_tutor_earnings_total ?? 0;
  const completed = course.sessions_completed ?? 0;
  const remaining = course.sessions_remaining ?? course.planned_session_count ?? 0;
  const approved = course.sessions_earning_approved ?? 0;
  const cancelled = (course.status === 'cancelled');

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`course-card-${course.course_id}`}>
      <div className="p-4 bg-white">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={onToggle} className="text-gray-500 hover:text-black" data-testid={`course-toggle-${course.course_id}`}>
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              <div className="text-sm font-bold text-gray-900">{course.subject}</div>
              {cancelled && <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">Cancelled</Badge>}
            </div>
            <div className="mt-1 text-xs text-gray-600 space-y-0.5">
              <div><GraduationCap className="h-3 w-3 inline mr-1 text-gray-400" /> {course.student_name} · <span className="text-gray-500">{course.student_email}</span></div>
              <div><User className="h-3 w-3 inline mr-1 text-gray-400" /> {course.tutor_name} · <span className="text-gray-500">{course.tutor_email}</span>
                {course.is_multi_tutor && (course.modules || []).length > 0 && (
                  <Badge className="ml-2 text-[9px] bg-red-700 hover:bg-red-700">Multi-tutor</Badge>
                )}
              </div>
              {course.is_multi_tutor && (course.modules || []).filter((m) => m.status !== 'removed').map((m) => (
                <div key={m.module_id} className="pl-4 text-[11px] text-gray-600">
                  <span className="text-gray-400">↳</span> <span className="font-semibold text-gray-800">{m.module_name}</span> · {m.tutor_name} <span className="text-gray-400">({m.tutor_email})</span> · {(m.selected_weekday_labels || []).join(', ')} · {m.class_start_time} · {m.planned_session_count} × {m.session_duration_minutes} min
                </div>
              ))}
              <div>
                <Calendar className="h-3 w-3 inline mr-1 text-gray-400" />
                {course.start_date} → {course.end_date} · {(course.selected_weekday_labels || []).join(', ')} · {course.class_start_time} · {course.session_duration_minutes} min
              </div>
              <div>
                <DollarSign className="h-3 w-3 inline mr-1 text-gray-400" />
                {curr} {course.tutor_hourly_rate}/hr · Planned {course.planned_session_count} · Completed {completed} · Remaining {remaining}
              </div>
            </div>
          </div>

          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Tutor earnings</div>
            <div className="text-sm text-gray-500">Projected <span className="font-bold text-gray-900" data-testid={`course-projected-${course.course_id}`}>{curr} {projected}</span></div>
            <div className="text-sm text-gray-500">Confirmed <span className="font-bold text-green-700" data-testid={`course-confirmed-${course.course_id}`}>{curr} {confirmed}</span></div>
            <div className="text-[10px] text-gray-400">{approved} of {course.planned_session_count} sessions approved</div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => onCopyLink(course.student_join_url, 'Student')}
            data-testid={`course-copy-student-${course.course_id}`}>
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy student link
          </Button>
          <Button size="sm" variant="outline" onClick={() => onCopyLink(course.tutor_join_url, 'Tutor')}
            data-testid={`course-copy-tutor-${course.course_id}`}>
            <Copy className="h-3.5 w-3.5 mr-1" /> Copy tutor link
          </Button>
          <Button size="sm" variant="outline" onClick={() => onSendInvites()} data-testid={`course-send-invites-${course.course_id}`}>
            <Mail className="h-3.5 w-3.5 mr-1" /> Send invites (both)
          </Button>
          <Button size="sm" variant="outline" onClick={onReviewInvites} data-testid={`course-review-invites-${course.course_id}`}>
            <FileText className="h-3.5 w-3.5 mr-1" /> Review &amp; edit invites
          </Button>
          <Button size="sm" variant="outline" onClick={() => onSendInvites('student')} data-testid={`course-send-student-${course.course_id}`}>
            <Mail className="h-3.5 w-3.5 mr-1" /> Send student only
          </Button>
          <Button size="sm" variant="outline" onClick={() => onSendInvites('tutor')} data-testid={`course-send-tutor-${course.course_id}`}>
            <Mail className="h-3.5 w-3.5 mr-1" /> Send tutor only
          </Button>
          <Button size="sm" onClick={onGeneratePayment} className="bg-green-700 hover:bg-green-800 text-white"
            data-testid={`course-generate-payment-${course.course_id}`}>
            <DollarSign className="h-3.5 w-3.5 mr-1" /> Generate payment link
          </Button>
          {!cancelled && (
            <Button size="sm" variant="outline" onClick={onEditCourse} data-testid={`course-edit-${course.course_id}`}>
              <Edit className="h-3.5 w-3.5 mr-1" /> Edit course
            </Button>
          )}
          {!cancelled && (
            <Button size="sm" variant="outline" onClick={onManageTutors} data-testid={`course-manage-tutors-${course.course_id}`}>
              <Users className="h-3.5 w-3.5 mr-1" /> Manage tutors
            </Button>
          )}
          {!cancelled && (
            <Button size="sm" variant="outline" onClick={onCancel}
              className="text-red-700 border-red-200 hover:bg-red-50"
              data-testid={`course-cancel-${course.course_id}`}>
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Cancel course
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50/70 p-3" data-testid={`course-sessions-${course.course_id}`}>
          {loadingSessions ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading sessions…
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-xs italic text-gray-400 py-4 text-center">No sessions.</div>
          ) : (
            <div className="space-y-2">
              {sessions.map((s) => (
                <SessionRow key={s.session_id} session={s} currency={curr} onPatch={(patch) => onPatchSession(s.session_id, patch)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SessionRow — one session with attendance + earning controls
// ============================================================================

const STATUS_OPTIONS = ['scheduled', 'completed', 'cancelled', 'rescheduled'];
const ATTEND_OPTIONS = ['', 'present', 'absent', 'late'];

const SessionRow = ({ session, currency, onPatch }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...session });
  const isCompletedApproved = session.status === 'completed' && session.earning_approved;

  const save = async () => {
    const patch = {};
    ['status', 'student_attendance', 'tutor_attendance', 'actual_duration_minutes', 'notes', 'payment_status', 'scheduled_start', 'scheduled_end']
      .forEach((k) => { if (draft[k] !== session[k]) patch[k] = draft[k]; });
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    await onPatch(patch);
    setEditing(false);
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-3" data-testid={`session-row-${session.session_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm">
          <span className="font-bold">Session #{session.session_number}</span>
          <span className="text-gray-500 ml-2">{session.day_of_week} · {session.scheduled_date} · {(session.scheduled_start || '').slice(11, 16)}–{(session.scheduled_end || '').slice(11, 16)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-[10px] ${session.status === 'completed' ? 'border-green-300 text-green-700' : session.status === 'cancelled' ? 'border-red-300 text-red-700' : 'border-gray-300 text-gray-600'}`}>
            {session.status}
          </Badge>
          {isCompletedApproved ? (
            <Badge className="text-[10px] bg-green-600">Earning approved · {currency} {session.tutor_earning}</Badge>
          ) : session.status === 'completed' ? (
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">Pending approval</Badge>
          ) : null}
        </div>
      </div>

      {!editing ? (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-gray-600">
          <div>Student: <strong>{session.student_attendance || '—'}</strong></div>
          <div>Tutor: <strong>{session.tutor_attendance || '—'}</strong></div>
          <div>Duration: <strong>{session.actual_duration_minutes || session.duration_minutes} min</strong></div>
          <div>Payment: <strong>{session.payment_status || 'pending'}</strong></div>
          {session.notes ? <div className="col-span-full text-gray-500 italic">&ldquo;{session.notes}&rdquo;</div> : null}
        </div>
      ) : (
        <div className="mt-3 grid sm:grid-cols-2 gap-2">
          <SmallField label="Status">
            <select value={draft.status || 'scheduled'} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
              className="w-full h-8 px-2 rounded border border-gray-300 text-xs" data-testid={`session-status-${session.session_id}`}>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </SmallField>
          <SmallField label="Duration (min actual)">
            <Input type="number" value={draft.actual_duration_minutes ?? ''} onChange={(e) => setDraft({ ...draft, actual_duration_minutes: parseInt(e.target.value, 10) || null })}
              placeholder={String(session.duration_minutes)} className="h-8 text-xs" />
          </SmallField>
          <SmallField label="Student attendance">
            <select value={draft.student_attendance || ''} onChange={(e) => setDraft({ ...draft, student_attendance: e.target.value || null })}
              className="w-full h-8 px-2 rounded border border-gray-300 text-xs" data-testid={`session-student-att-${session.session_id}`}>
              {ATTEND_OPTIONS.map((a) => <option key={a} value={a}>{a || '—'}</option>)}
            </select>
          </SmallField>
          <SmallField label="Tutor attendance">
            <select value={draft.tutor_attendance || ''} onChange={(e) => setDraft({ ...draft, tutor_attendance: e.target.value || null })}
              className="w-full h-8 px-2 rounded border border-gray-300 text-xs" data-testid={`session-tutor-att-${session.session_id}`}>
              {ATTEND_OPTIONS.map((a) => <option key={a} value={a}>{a || '—'}</option>)}
            </select>
          </SmallField>
          <SmallField label="Payment status">
            <select value={draft.payment_status || 'pending'} onChange={(e) => setDraft({ ...draft, payment_status: e.target.value })}
              className="w-full h-8 px-2 rounded border border-gray-300 text-xs">
              <option value="pending">pending</option>
              <option value="paid">paid</option>
              <option value="waived">waived</option>
              <option value="refunded">refunded</option>
            </select>
          </SmallField>
          <SmallField label="Scheduled start (ISO)">
            <Input value={draft.scheduled_start || ''} onChange={(e) => setDraft({ ...draft, scheduled_start: e.target.value })} className="h-8 text-xs" />
          </SmallField>
          <div className="sm:col-span-2">
            <SmallField label="Admin notes">
              <Textarea value={draft.notes || ''} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={2} className="text-xs" />
            </SmallField>
          </div>
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-1.5 justify-end">
        {editing ? (
          <>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft({ ...session }); }} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={save} className="bg-red-700 hover:bg-red-800 text-white text-xs" data-testid={`session-save-${session.session_id}`}>
              <Check className="h-3 w-3 mr-1" /> Save
            </Button>
          </>
        ) : (
          <>
            {session.status !== 'completed' && (
              <Button size="sm" variant="outline" onClick={() => onPatch({ status: 'completed' })}
                className="text-xs" data-testid={`session-mark-completed-${session.session_id}`}>
                Mark completed
              </Button>
            )}
            {session.status === 'completed' && !session.earning_approved && (
              <Button size="sm" onClick={() => onPatch({ earning_approved: true })}
                className="bg-green-600 hover:bg-green-700 text-white text-xs"
                data-testid={`session-approve-earning-${session.session_id}`}>
                <DollarSign className="h-3 w-3 mr-1" /> Approve earning
              </Button>
            )}
            {session.status === 'completed' && session.earning_approved && (
              <Button size="sm" variant="outline" onClick={() => onPatch({ earning_approved: false })}
                className="text-xs" data-testid={`session-revoke-earning-${session.session_id}`}>
                Revoke approval
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => { setEditing(true); setDraft({ ...session }); }} className="text-xs"
              data-testid={`session-edit-${session.session_id}`}>
              Edit
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const SmallField = ({ label, children }) => (
  <label className="block">
    <span className="text-[9px] uppercase tracking-wider text-gray-500 font-bold block mb-0.5">{label}</span>
    {children}
  </label>
);

// ============================================================================
// CreateCourseModal — the new booking form with searchable dropdowns
// ============================================================================

const initialForm = () => ({
  student_email: '', student_name: '', student_id: '',
  tutor_email: '', tutor_name: '', tutor_id: '',
  subject: '',
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
  selected_weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
  class_start_time: '17:00',
  session_duration_minutes: 60,
  planned_session_count: 8,
  tutor_hourly_rate: 40,
  tutor_hourly_rate_currency: 'USD',
  timezone: 'Australia/Melbourne',
  notes: '',
});

const CreateCourseModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(initialForm());
  const [busy, setBusy] = useState(false);
  const [manualStudent, setManualStudent] = useState(false);
  const [manualTutor, setManualTutor] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleWeekday = (key) => {
    setForm((p) => ({
      ...p,
      selected_weekdays: p.selected_weekdays.includes(key)
        ? p.selected_weekdays.filter((w) => w !== key)
        : [...p.selected_weekdays, key],
    }));
  };

  const submit = async () => {
    if (!form.student_email || !form.tutor_email || !form.subject) {
      toast.error('Please select a student, a tutor and provide a subject.'); return;
    }
    if (!form.selected_weekdays.length) { toast.error('Select at least one weekday.'); return; }
    if (!form.planned_session_count || form.planned_session_count <= 0) { toast.error('Planned sessions must be > 0'); return; }
    setBusy(true);
    try {
      const r = await ax.post('/api/admin/course-bookings', {
        ...form,
        planned_session_count: parseInt(form.planned_session_count, 10),
        session_duration_minutes: parseInt(form.session_duration_minutes, 10),
        tutor_hourly_rate: parseFloat(form.tutor_hourly_rate),
      });
      toast.success(`Course created · ${r.data?.session_count} sessions generated`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Course creation failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose} data-testid="course-create-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
            <h3 className="text-lg font-bold text-gray-900 mt-1">Create course booking</h3>
            <p className="text-xs text-gray-500 mt-1">Generates every scheduled session and one reusable classroom link.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        {/* Student */}
        <div className="mt-5">
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Student *</Label>
            <button className="text-[10px] text-red-700 hover:underline" onClick={() => setManualStudent((m) => !m)}
              data-testid="toggle-manual-student">
              {manualStudent ? 'Use registered list' : 'Add manually'}
            </button>
          </div>
          {manualStudent ? (
            <div className="grid sm:grid-cols-2 gap-2">
              <Input placeholder="Student email" value={form.student_email} onChange={(e) => set('student_email', e.target.value)} data-testid="course-student-email-manual" />
              <Input placeholder="Student full name" value={form.student_name} onChange={(e) => set('student_name', e.target.value)} data-testid="course-student-name-manual" />
            </div>
          ) : (
            <UserSearchSelect
              endpoint="/api/admin/lookup/students"
              placeholder="Search by name or email…"
              selectedEmail={form.student_email}
              onSelect={(u) => setForm((p) => ({ ...p, student_email: u.email, student_name: u.name, student_id: u.id || '' }))}
              testId="course-student-search"
            />
          )}
        </div>

        {/* Tutor */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Tutor *</Label>
            <button className="text-[10px] text-red-700 hover:underline" onClick={() => setManualTutor((m) => !m)}
              data-testid="toggle-manual-tutor">
              {manualTutor ? 'Use approved list' : 'Add manually'}
            </button>
          </div>
          {manualTutor ? (
            <div className="grid sm:grid-cols-2 gap-2">
              <Input placeholder="Tutor email" value={form.tutor_email} onChange={(e) => set('tutor_email', e.target.value)} data-testid="course-tutor-email-manual" />
              <Input placeholder="Tutor full name" value={form.tutor_name} onChange={(e) => set('tutor_name', e.target.value)} data-testid="course-tutor-name-manual" />
            </div>
          ) : (
            <UserSearchSelect
              endpoint="/api/admin/lookup/tutors"
              placeholder="Search by name, subject or email…"
              selectedEmail={form.tutor_email}
              onSelect={(u) => setForm((p) => ({ ...p, tutor_email: u.email, tutor_name: u.name, tutor_id: u.id || '' }))}
              testId="course-tutor-search"
              secondaryText={(u) => u.subjects || u.public_display_name || u.email}
            />
          )}
        </div>

        {/* Course fields */}
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Subject *"><Input value={form.subject} onChange={(e) => set('subject', e.target.value)} placeholder="e.g. IELTS Writing" data-testid="course-subject" /></Field>
          <Field label="Start date *"><Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="course-start-date" /></Field>
          <Field label="End date *"><Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} data-testid="course-end-date" /></Field>
          <Field label="Class start time *"><Input type="time" value={form.class_start_time} onChange={(e) => set('class_start_time', e.target.value)} data-testid="course-start-time" /></Field>
          <Field label="Duration (min)"><Input type="number" value={form.session_duration_minutes} onChange={(e) => set('session_duration_minutes', e.target.value)} data-testid="course-duration" /></Field>
          <Field label="Planned sessions *"><Input type="number" value={form.planned_session_count} onChange={(e) => set('planned_session_count', e.target.value)} data-testid="course-planned-count" /></Field>
          <Field label="Tutor hourly rate">
            <div className="flex gap-1">
              <Input type="number" value={form.tutor_hourly_rate} onChange={(e) => set('tutor_hourly_rate', e.target.value)} data-testid="course-hourly-rate" />
              <select value={form.tutor_hourly_rate_currency} onChange={(e) => set('tutor_hourly_rate_currency', e.target.value)}
                className="h-9 px-2 rounded border border-gray-300 text-sm" data-testid="course-hourly-currency">
                {['USD', 'AUD', 'KES', 'NGN', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </Field>
          <Field label="Timezone"><Input value={form.timezone} onChange={(e) => set('timezone', e.target.value)} /></Field>
        </div>

        {/* Weekdays */}
        <div className="mt-4">
          <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1.5">Selected weekdays *</Label>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => toggleWeekday(w.key)}
                aria-pressed={form.selected_weekdays.includes(w.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  form.selected_weekdays.includes(w.key)
                    ? 'bg-red-700 border-red-700 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`course-weekday-${w.key}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Field label="Notes (optional)">
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} data-testid="course-notes" />
          </Field>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="course-create-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="course-create-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <LinkIcon className="h-4 w-4 mr-1" />}
            Create course &amp; generate sessions
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// UserSearchSelect — debounced dropdown for student / tutor lookup
// ============================================================================

const UserSearchSelect = ({ endpoint, placeholder, selectedEmail, onSelect, testId, secondaryText }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chosenLabel, setChosenLabel] = useState('');
  const boxRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await ax.get(endpoint, { params: { q, limit: 15 } });
        setItems(r.data?.items || []);
      } catch { setItems([]); } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, endpoint]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <Input
          value={selectedEmail && !open ? (chosenLabel || selectedEmail) : q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={placeholder}
          className="pl-9"
          data-testid={testId}
        />
        {selectedEmail && !open && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-green-700 font-bold">Selected</div>
        )}
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-3 text-xs text-gray-500 flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…</div>
          ) : items.length === 0 ? (
            <div className="p-3 text-xs italic text-gray-400">No matches. Try a different search or use “Add manually”.</div>
          ) : (
            items.map((u) => (
              <button
                key={u.id || u.email}
                type="button"
                onClick={() => {
                  onSelect(u);
                  setChosenLabel(`${u.name} — ${u.email}`);
                  setOpen(false);
                  setQ('');
                }}
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 ${u.email === selectedEmail ? 'bg-red-50/50' : ''}`}
                data-testid={`${testId}-item-${u.email}`}
              >
                <div className="text-sm font-medium text-gray-900">{u.name}</div>
                <div className="text-[11px] text-gray-500">{u.email}{secondaryText ? ` · ${secondaryText(u)}` : ''}</div>
              </button>
            ))
          )}
        </div>
      )}
      {selectedEmail && (
        <div className="mt-1 text-[10px] text-gray-500">
          Email auto-filled: <span className="font-mono text-gray-700" data-testid={`${testId}-selected-email`}>{selectedEmail}</span>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block text-sm">
    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">{label}</span>
    {children}
  </label>
);

// ============================================================================
// GeneratePaymentLinkModal — Parts 1+2 payment-link creator (student/parent only)
// ============================================================================

const PAY_OPTIONS = [
  { v: 'full_payment', l: 'Full payment' },
  { v: 'deposit', l: 'Deposit' },
  { v: 'weekly', l: 'Weekly instalment' },
  { v: 'fortnightly', l: 'Fortnightly instalment' },
  { v: 'monthly', l: 'Monthly instalment' },
  { v: 'custom', l: 'Custom amount' },
];

const GeneratePaymentLinkModal = ({ course, onClose, onGenerated }) => {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [form, setForm] = useState({
    payment_option: 'full_payment',
    amount: '',
    currency: 'AUD',
    description: `${course.subject} — payment`,
    package_name: 'Intensive Package',
    due_date: '',
    recipient_email: course.student_email || '',
    parent_email: '',
    discount: '0',
    scholarship: '0',
    note: '',
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Load payment summary + suggested amounts.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSummary(true);
      try {
        const r = await ax.get(`/api/admin/course-bookings/${course.course_id}/payment-summary`);
        if (cancelled) return;
        setSummary(r.data);
        setForm((p) => ({
          ...p,
          amount: String(r.data?.suggested_amounts?.full_payment ?? p.amount),
          currency: r.data?.currency || p.currency,
          parent_email: r.data?.parent_email || p.parent_email,
          recipient_email: r.data?.student_email || p.recipient_email,
        }));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load payment summary');
      } finally { if (!cancelled) setLoadingSummary(false); }
    })();
    return () => { cancelled = true; };
  }, [course.course_id]);

  // When admin flips payment option, prefill the suggested amount for that cadence.
  const onOptionChange = (v) => {
    const suggested = summary?.suggested_amounts?.[v];
    setForm((p) => ({
      ...p,
      payment_option: v,
      amount: (suggested !== undefined && v !== 'custom') ? String(suggested) : p.amount,
      description: `${course.subject} — ${PAY_OPTIONS.find((o) => o.v === v)?.l || v}`,
    }));
  };

  const num = (x) => (isNaN(parseFloat(x)) ? 0 : parseFloat(x));
  const discount = num(form.discount);
  const scholarship = num(form.scholarship);
  const amount = num(form.amount);
  const packageTotal = summary?.package_total ?? 0;
  const paidAmount = summary?.paid_amount ?? 0;
  const netOutstanding = Math.max(
    0,
    Math.round((packageTotal - paidAmount - discount - scholarship - amount) * 100) / 100,
  );
  const chargeAmount = Math.max(0, Math.round((amount - discount - scholarship) * 100) / 100);

  const submit = async () => {
    if (chargeAmount <= 0) { toast.error('Amount to charge must be > 0 (after discount / scholarship)'); return; }
    if (!form.recipient_email) { toast.error('Recipient email required'); return; }
    setBusy(true);
    try {
      const r = await ax.post(`/api/admin/course-bookings/${course.course_id}/generate-payment-link`, {
        payment_option: form.payment_option,
        amount: chargeAmount,
        currency: form.currency,
        description: form.description,
        due_date: form.due_date || null,
        recipient_email: form.recipient_email,
        parent_email: form.parent_email || null,
        package_name: form.package_name,
        discount, scholarship,
        note: form.note,
      });
      setResult(r.data);
      toast.success('Payment link generated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Generate failed'); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!result?.authorization_url) return;
    navigator.clipboard?.writeText(result.authorization_url).then(
      () => toast.success('Payment link copied'),
      () => toast.error('Copy failed'),
    );
  };

  const [emailing, setEmailing] = useState(false);

  const emailLink = async () => {
    if (!result?.payment_link?.id) return;
    setEmailing(true);
    try {
      const r = await ax.post(
        `/api/admin/course-bookings/payment-links/${result.payment_link.id}/send-email`,
        // no `to` — server falls back to link.recipient_email (which is the
        // email the admin picked in the review modal, student or parent).
        { payment_link_id: result.payment_link.id },
      );
      if (r.data?.ok) toast.success(`Payment link emailed to ${r.data.to}`);
      else toast.error(`Send failed${r.data?.outcome?.reason ? ': ' + r.data.outcome.reason : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setEmailing(false); }
  };

  const whatsappLink = () => {
    const msg = encodeURIComponent(
      `Winhouse Tutors — payment for ${course.subject}. Secure link: ${result.authorization_url} (${result.payment_link.currency} ${result.payment_link.amount})`,
    );
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const previewPaymentPage = () => {
    if (!result?.authorization_url) return;
    window.open(result.authorization_url, '_blank', 'noopener,noreferrer');
  };

  const regenerateLink = () => {
    // Return to the review form so the admin can adjust anything and issue a
    // fresh Paystack link. The previous link stays valid in
    // `course_payment_links` and remains listed under the course.
    setResult(null);
    toast.info('Adjust details and generate a new link. The previous link remains valid.');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="generate-payment-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
            <h3 className="text-lg font-bold text-gray-900 mt-1">Payment Review</h3>
            <div className="text-xs text-gray-500 mt-1">Payment link will be sent to student / parent only.</div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        {!result ? (
          loadingSummary ? (
            <div className="py-16 flex items-center justify-center text-sm text-gray-500 gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading course details…
            </div>
          ) : (
            <>
              {/* Read-only summary block */}
              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs space-y-1">
                <div className="flex justify-between"><span className="text-gray-500">Student</span>
                  <span className="font-semibold text-gray-900" data-testid="pay-review-student">{summary?.student_name || course.student_name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Course</span>
                  <span className="font-semibold text-gray-900">{summary?.subject || course.subject}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Sessions</span>
                  <span className="font-mono">{summary?.planned_session_count} × {summary?.currency} {summary?.session_rate}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Package Total</span>
                  <span className="font-mono" data-testid="pay-review-package-total">{summary?.currency} {(summary?.package_total || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Paid so far</span>
                  <span className="font-mono">{summary?.currency} {(summary?.paid_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Outstanding</span>
                  <span className="font-mono text-red-700 font-bold" data-testid="pay-review-outstanding">{summary?.currency} {(summary?.outstanding || 0).toFixed(2)}</span></div>
              </div>

              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Package">
                    <Input value={form.package_name} onChange={(e) => set('package_name', e.target.value)} data-testid="pay-review-package" />
                  </Field>
                  <Field label="Currency">
                    <select value={form.currency} onChange={(e) => set('currency', e.target.value)}
                      className="w-full h-9 px-2 rounded border border-gray-300 text-sm" data-testid="pay-review-currency">
                      {['AUD', 'USD', 'KES', 'NGN', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Payment Type">
                  <select value={form.payment_option} onChange={(e) => onOptionChange(e.target.value)}
                    className="w-full h-9 px-2 rounded border border-gray-300 text-sm" data-testid="payment-option">
                    {PAY_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Amount (editable)">
                    <Input type="number" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="payment-amount" />
                  </Field>
                  <Field label="Due date">
                    <Input type="date" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} data-testid="pay-review-due-date" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Discount (optional)">
                    <Input type="number" step="0.01" value={form.discount} onChange={(e) => set('discount', e.target.value)} data-testid="pay-review-discount" />
                  </Field>
                  <Field label="Scholarship (optional)">
                    <Input type="number" step="0.01" value={form.scholarship} onChange={(e) => set('scholarship', e.target.value)} data-testid="pay-review-scholarship" />
                  </Field>
                </div>
                <div className="rounded-md border border-dashed border-gray-300 p-2 text-[11px] flex justify-between bg-white">
                  <span className="text-gray-500">Remaining outstanding after this link</span>
                  <span className="font-mono font-bold" data-testid="pay-review-outstanding-after">
                    {summary?.currency} {netOutstanding.toFixed(2)}
                  </span>
                </div>
                <Field label="Description">
                  <Input value={form.description} onChange={(e) => set('description', e.target.value)} data-testid="pay-review-description" />
                </Field>
                <div className="grid grid-cols-1 gap-2">
                  <Field label="Email Recipient">
                    <Input value={form.recipient_email} onChange={(e) => set('recipient_email', e.target.value)} data-testid="pay-review-recipient" />
                  </Field>
                  <Field label="Parent Email">
                    <Input value={form.parent_email} onChange={(e) => set('parent_email', e.target.value)} data-testid="pay-review-parent-email" />
                  </Field>
                </div>
                <Field label="Note (optional)">
                  <Textarea rows={2} value={form.note} onChange={(e) => set('note', e.target.value)} />
                </Field>

                {/* Preview */}
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-red-800 font-bold">Preview · Amount to charge</div>
                    <div className="text-[10px] text-gray-500">Amount − Discount − Scholarship</div>
                  </div>
                  <div className="text-xl font-bold text-red-800 font-mono" data-testid="pay-review-charge">
                    {form.currency} {chargeAmount.toFixed(2)}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="outline" onClick={onClose} data-testid="pay-review-cancel">Cancel</Button>
                  <Button onClick={submit} disabled={busy || chargeAmount <= 0}
                    className="bg-green-700 hover:bg-green-800 text-white" data-testid="payment-generate-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <DollarSign className="h-4 w-4 mr-1" />}
                    Generate Payment Link
                  </Button>
                </div>
              </div>
            </>
          )
        ) : (
          <div className="mt-5 space-y-3">
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm break-all">
              <div className="text-[10px] uppercase tracking-wider text-green-800 font-bold">Payment link</div>
              <a href={result.authorization_url} target="_blank" rel="noopener noreferrer" className="text-green-800 underline" data-testid="payment-link-url">
                {result.authorization_url}
              </a>
              <div className="mt-1 text-xs text-gray-600">Reference: {result.reference}</div>
              <div className="mt-1 text-xs text-gray-600">Recipient: <strong>{result.recipient_email}</strong></div>
              <div className="mt-1 text-xs text-gray-600">Amount: <strong>{result.payment_link.currency} {result.payment_link.amount}</strong></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={copyLink} data-testid="payment-copy"><Copy className="h-3.5 w-3.5 mr-1" /> Copy link</Button>
              <Button size="sm" variant="outline" onClick={emailLink} disabled={emailing} data-testid="payment-email">
                {emailing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                Email link
              </Button>
              <Button size="sm" variant="outline" onClick={whatsappLink} data-testid="payment-whatsapp">WhatsApp link</Button>
              <Button size="sm" variant="outline" onClick={previewPaymentPage} data-testid="payment-preview-page">
                <LinkIcon className="h-3.5 w-3.5 mr-1" /> Preview payment page
              </Button>
              <Button size="sm" variant="outline" onClick={regenerateLink} data-testid="payment-regenerate">
                <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Regenerate link
              </Button>
              <Button size="sm" onClick={onGenerated} className="bg-red-700 hover:bg-red-800 text-white ml-auto">Done</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================================================
// ReviewInvitesModal — preview + edit subject/body + PDFs before sending
// ============================================================================

const ReviewInvitesModal = ({ course, onClose, onSent }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'student' | 'tutor' | 'both' | null
  const [attachPdf, setAttachPdf] = useState(true);
  const [studentSubject, setStudentSubject] = useState('');
  const [studentBody, setStudentBody] = useState('');
  const [tutorSubject, setTutorSubject] = useState('');
  const [tutorBody, setTutorBody] = useState('');
  const [outcomes, setOutcomes] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await ax.get(`/api/admin/course-bookings/${course.course_id}/invite-preview`);
        if (cancelled) return;
        setPreview(r.data);
        setStudentSubject(r.data?.student?.subject || '');
        setStudentBody(r.data?.student?.body_html || '');
        setTutorSubject(r.data?.tutor?.subject || '');
        setTutorBody(r.data?.tutor?.body_html || '');
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not load preview');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [course.course_id]);

  const openPdf = (role) => {
    const url = `${BACKEND_URL}/api/admin/course-bookings/${course.course_id}/timetable.pdf?role=${role}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const send = async (target) => {
    setBusy(target || 'both');
    try {
      const body = {
        target: target || null,
        student_subject: studentSubject,
        student_body_html: studentBody,
        tutor_subject: tutorSubject,
        tutor_body_html: tutorBody,
        attach_pdf: attachPdf,
      };
      const r = await ax.post(`/api/admin/course-bookings/${course.course_id}/send-invites-v2`, body);
      setOutcomes(r.data?.outcomes || {});
      if (r.data?.ok) toast.success(`Invites sent · ${(r.data?.sent || []).join(', ')}`);
      else if ((r.data?.sent || []).length) toast.warning(`Partial: sent ${r.data.sent.join(', ')}; failed ${r.data.failed.join(', ')}`);
      else toast.error(`Send failed: ${(r.data?.failed || []).join(', ') || 'unknown'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="review-invites-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
            <h3 className="text-lg font-bold text-gray-900 mt-1">Review &amp; send invites</h3>
            <p className="text-xs text-gray-500 mt-1">Preview PDFs and edit each recipient&apos;s email before sending. Times are shown in each recipient&apos;s local timezone.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-sm text-gray-500 gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
          </div>
        ) : (
          <>
            <div className="mt-4 flex items-center gap-2">
              <input id="attach-pdf" type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} data-testid="review-attach-pdf" />
              <label htmlFor="attach-pdf" className="text-xs text-gray-700">Attach role-specific PDF timetable</label>
            </div>

            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <RecipientPanel
                role="student"
                title="Student invite"
                recipient={preview?.student?.recipient_email}
                timezone={preview?.student?.timezone}
                subject={studentSubject}
                setSubject={setStudentSubject}
                body={studentBody}
                setBody={setStudentBody}
                onOpenPdf={() => openPdf('student')}
                outcome={outcomes.student}
                sending={busy === 'student' || busy === 'both'}
                onSend={() => send('student')}
                testPrefix="review-student"
              />
              <RecipientPanel
                role="tutor"
                title="Tutor invite"
                recipient={preview?.tutor?.recipient_email}
                timezone={preview?.tutor?.timezone}
                subject={tutorSubject}
                setSubject={setTutorSubject}
                body={tutorBody}
                setBody={setTutorBody}
                onOpenPdf={() => openPdf('tutor')}
                outcome={outcomes.tutor}
                sending={busy === 'tutor' || busy === 'both'}
                onSend={() => send('tutor')}
                testPrefix="review-tutor"
              />
            </div>

            <div className="mt-5 flex justify-between items-center gap-2">
              <div className="text-[11px] text-gray-500">
                Payment links are sent separately (never included in these invites).
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose} data-testid="review-close">Close</Button>
                <Button onClick={() => send(null)} disabled={!!busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="review-send-both">
                  {busy === 'both' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Mail className="h-4 w-4 mr-1" />}
                  Send both
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const RecipientPanel = ({ role, title, recipient, timezone, subject, setSubject, body, setBody, onOpenPdf, outcome, sending, onSend, testPrefix }) => (
  <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50" data-testid={`${testPrefix}-panel`}>
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs font-bold text-gray-900">{title}</div>
        <div className="text-[11px] text-gray-500 mt-0.5">To: <span className="font-mono">{recipient || '—'}</span></div>
        <div className="text-[10px] text-gray-500">Times shown in: <span className="font-mono">{timezone || '—'}</span></div>
      </div>
      <div className="flex flex-col items-end gap-1">
        <Button size="sm" variant="outline" onClick={onOpenPdf} data-testid={`${testPrefix}-open-pdf`}>
          <FileText className="h-3.5 w-3.5 mr-1" /> Preview PDF
        </Button>
        {outcome && (
          outcome.sent
            ? <Badge className="text-[10px] bg-green-600">Sent</Badge>
            : <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">Failed</Badge>
        )}
      </div>
    </div>
    <div className="mt-3">
      <SmallField label="Subject">
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="h-8 text-xs" data-testid={`${testPrefix}-subject`} />
      </SmallField>
    </div>
    <div className="mt-2">
      <SmallField label="Body (HTML)">
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="font-mono text-[11px]" data-testid={`${testPrefix}-body`} />
      </SmallField>
    </div>
    <div className="mt-3 flex justify-end">
      <Button size="sm" onClick={onSend} disabled={sending} className="bg-red-700 hover:bg-red-800 text-white" data-testid={`${testPrefix}-send`}>
        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
        Send {role} only
      </Button>
    </div>
  </div>
);

// ============================================================================
// EditCourseModal — safe edit that preserves completed sessions / classroom
// ============================================================================

const EditCourseModal = ({ course, onClose, onSaved }) => {
  const [form, setForm] = useState({
    subject: course.subject || '',
    start_date: course.start_date || '',
    end_date: course.end_date || '',
    selected_weekdays: course.selected_weekday_labels || [],
    class_start_time: course.class_start_time || '',
    session_duration_minutes: course.session_duration_minutes || 60,
    planned_session_count: course.planned_session_count || 0,
    tutor_hourly_rate: course.tutor_hourly_rate || 0,
    tutor_hourly_rate_currency: course.tutor_hourly_rate_currency || 'USD',
    notes: course.notes || '',
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleWeekday = (key) => setForm((p) => ({
    ...p,
    selected_weekdays: p.selected_weekdays.includes(key)
      ? p.selected_weekdays.filter((w) => w !== key)
      : [...p.selected_weekdays, key],
  }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        planned_session_count: parseInt(form.planned_session_count, 10),
        session_duration_minutes: parseInt(form.session_duration_minutes, 10),
        tutor_hourly_rate: parseFloat(form.tutor_hourly_rate),
      };
      const r = await ax.patch(`/api/admin/course-bookings/${course.course_id}`, payload);
      const parts = [
        `${(r.data?.sessions_updated || []).length} updated`,
        `${(r.data?.sessions_inserted || []).length} new`,
        `${(r.data?.sessions_cancelled || []).length} cancelled`,
        `${(r.data?.sessions_completed_preserved || []).length} completed preserved`,
      ];
      toast.success(`Course updated · ${parts.join(', ')}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-course-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
            <h3 className="text-lg font-bold text-gray-900 mt-1">Edit course</h3>
            <p className="text-xs text-gray-500 mt-1">
              Completed sessions, earnings, classroom links and payment links are preserved. Only future/scheduled sessions will be recalculated.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Subject"><Input value={form.subject} onChange={(e) => set('subject', e.target.value)} data-testid="edit-subject" /></Field>
          <Field label="Start date"><Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="edit-start-date" /></Field>
          <Field label="End date"><Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} data-testid="edit-end-date" /></Field>
          <Field label="Class start time"><Input type="time" value={form.class_start_time} onChange={(e) => set('class_start_time', e.target.value)} data-testid="edit-start-time" /></Field>
          <Field label="Duration (min)"><Input type="number" value={form.session_duration_minutes} onChange={(e) => set('session_duration_minutes', e.target.value)} data-testid="edit-duration" /></Field>
          <Field label="Planned sessions"><Input type="number" value={form.planned_session_count} onChange={(e) => set('planned_session_count', e.target.value)} data-testid="edit-planned-count" /></Field>
          <Field label="Tutor hourly rate">
            <div className="flex gap-1">
              <Input type="number" value={form.tutor_hourly_rate} onChange={(e) => set('tutor_hourly_rate', e.target.value)} data-testid="edit-hourly-rate" />
              <select value={form.tutor_hourly_rate_currency} onChange={(e) => set('tutor_hourly_rate_currency', e.target.value)}
                className="h-9 px-2 rounded border border-gray-300 text-sm">
                {['USD', 'AUD', 'KES', 'NGN', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </Field>
        </div>

        <div className="mt-4">
          <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1.5">Weekdays</Label>
          <div className="flex flex-wrap gap-1.5">
            {WEEKDAYS.map((w) => (
              <button
                key={w.key}
                type="button"
                onClick={() => toggleWeekday(w.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  form.selected_weekdays.includes(w.key)
                    ? 'bg-red-700 border-red-700 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`edit-weekday-${w.key}`}
              >{w.label}</button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Field label="Notes (optional)">
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} data-testid="edit-notes" />
          </Field>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="edit-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// ManageTutorsModal — multi-tutor assignment for a Course Booking
// ============================================================================

const ManageTutorsModal = ({ course, onClose, onChanged }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/api/admin/course-bookings/${course.course_id}/modules`);
      setModules(r.data?.items || []);
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [course.course_id]);

  const remove = async (m) => {
    if (m.legacy) { toast.warning('This is the original single-tutor assignment. Use Edit course to change it.'); return; }
    if (!window.confirm(`Remove ${m.tutor_name} from "${m.module_name}"? Future sessions will be cancelled; completed sessions and earnings are preserved.`)) return;
    try {
      const r = await ax.delete(`/api/admin/course-bookings/${course.course_id}/modules/${m.module_id}`);
      toast.success(`Removed · ${r.data?.future_sessions_cancelled || 0} future session(s) cancelled, ${r.data?.completed_preserved || 0} completed preserved`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="manage-tutors-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
            <h3 className="text-lg font-bold text-gray-900 mt-1">Manage tutors — {course.subject}</h3>
            <p className="text-xs text-gray-500 mt-1">One student · one course · multiple tutors by module. Existing sessions/earnings/classroom links preserved. Student payment stays attached to the course.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-sm text-gray-500 gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="mt-4 space-y-2">
              {modules.length === 0 && <div className="text-sm text-gray-500 border border-dashed border-gray-300 rounded p-4 text-center">No tutor assignments yet.</div>}
              {modules.map((m) => (
                editingId === m.module_id ? (
                  <ModuleForm
                    key={m.module_id}
                    course={course}
                    module={m}
                    onCancel={() => setEditingId(null)}
                    onSaved={() => { setEditingId(null); reload(); }}
                  />
                ) : (
                  <ModuleRow key={m.module_id || 'legacy'} m={m} onEdit={() => setEditingId(m.module_id)} onRemove={() => remove(m)} />
                )
              ))}
            </div>

            {adding ? (
              <ModuleForm
                course={course}
                module={null}
                onCancel={() => setAdding(false)}
                onSaved={() => { setAdding(false); reload(); }}
              />
            ) : (
              <div className="mt-4 flex justify-end">
                <Button onClick={() => setAdding(true)} className="bg-red-700 hover:bg-red-800 text-white" data-testid="mt-add-tutor">
                  <Plus className="h-4 w-4 mr-1" /> Add tutor
                </Button>
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <Button variant="outline" onClick={onClose} data-testid="mt-close">Close</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ModuleRow = ({ m, onEdit, onRemove }) => (
  <div className="border border-gray-200 rounded-lg p-3 flex items-start justify-between bg-white" data-testid={`mt-module-row-${m.module_id || 'legacy'}`}>
    <div className="text-xs space-y-1">
      <div className="font-bold text-gray-900 text-sm">{m.module_name}
        {m.legacy && <Badge variant="outline" className="ml-2 text-[9px] border-gray-300">Legacy · single tutor</Badge>}
        {m.status === 'removed' && <Badge variant="outline" className="ml-2 text-[9px] border-red-300 text-red-700">Removed</Badge>}
      </div>
      <div className="text-gray-600"><User className="h-3 w-3 inline mr-1 text-gray-400" /> {m.tutor_name} <span className="text-gray-500">({m.tutor_email})</span></div>
      {!m.legacy && (
        <div className="text-gray-500">
          {(m.selected_weekday_labels || []).join(', ')} · {m.class_start_time} · {m.planned_session_count} × {m.session_duration_minutes} min · {m.tutor_hourly_rate_currency} {m.tutor_hourly_rate}/hr
        </div>
      )}
    </div>
    {!m.legacy && m.status !== 'removed' && (
      <div className="flex gap-1 shrink-0">
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`mt-edit-${m.module_id}`}><Edit className="h-3 w-3" /></Button>
        <Button size="sm" variant="outline" onClick={onRemove} className="text-red-700 hover:text-red-800" data-testid={`mt-remove-${m.module_id}`}><Trash2 className="h-3 w-3" /></Button>
      </div>
    )}
  </div>
);

const ModuleForm = ({ course, module, onCancel, onSaved }) => {
  const [form, setForm] = useState({
    module_name: module?.module_name || '',
    tutor_email: module?.tutor_email || '',
    tutor_name: module?.tutor_name || '',
    tutor_id: module?.tutor_id || '',
    tutor_hourly_rate: module?.tutor_hourly_rate || 0,
    tutor_hourly_rate_currency: module?.tutor_hourly_rate_currency || course.tutor_hourly_rate_currency || 'USD',
    selected_weekdays: module?.selected_weekday_labels || [],
    class_start_time: module?.class_start_time || course.class_start_time || '',
    session_duration_minutes: module?.session_duration_minutes || course.session_duration_minutes || 60,
    planned_session_count: module?.planned_session_count || 4,
    notes: module?.notes || '',
    conflict_override_reason: '',
  });
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleWD = (k) => setForm((p) => ({ ...p, selected_weekdays: p.selected_weekdays.includes(k) ? p.selected_weekdays.filter((x) => x !== k) : [...p.selected_weekdays, k] }));

  const checkConflicts = async () => {
    setBusy(true);
    try {
      const r = await ax.post(`/api/admin/course-bookings/${course.course_id}/modules/check-conflicts`, {
        ...form,
        planned_session_count: parseInt(form.planned_session_count, 10),
        session_duration_minutes: parseInt(form.session_duration_minutes, 10),
        tutor_hourly_rate: parseFloat(form.tutor_hourly_rate),
      });
      setConflicts(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Conflict check failed'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!form.module_name || !form.tutor_email || form.selected_weekdays.length === 0) {
      toast.error('Module name, tutor and at least one weekday required'); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        planned_session_count: parseInt(form.planned_session_count, 10),
        session_duration_minutes: parseInt(form.session_duration_minutes, 10),
        tutor_hourly_rate: parseFloat(form.tutor_hourly_rate),
      };
      if (module) {
        await ax.patch(`/api/admin/course-bookings/${course.course_id}/modules/${module.module_id}`, payload);
        toast.success('Module updated');
      } else {
        const r = await ax.post(`/api/admin/course-bookings/${course.course_id}/modules`, payload);
        toast.success(`Module added · ${r.data?.sessions_added || 0} sessions generated`);
      }
      onSaved();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Save failed';
      if (String(detail).toLowerCase().includes('conflict')) {
        toast.error(detail + ' — click "Check conflicts" and fill in an override reason.');
      } else {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  const hasConflicts = conflicts && conflicts.has_conflicts;

  return (
    <div className="border-2 border-red-200 bg-red-50/30 rounded-lg p-4 mt-3 space-y-3" data-testid="mt-module-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Module name">
          <Input value={form.module_name} onChange={(e) => set('module_name', e.target.value)} placeholder="e.g. SAT Mathematics" data-testid="mt-module-name" />
        </Field>
        <Field label="Tutor email">
          <Input value={form.tutor_email} onChange={(e) => set('tutor_email', e.target.value)} placeholder="tutor@…" data-testid="mt-tutor-email" />
        </Field>
        <Field label="Tutor display name">
          <Input value={form.tutor_name} onChange={(e) => set('tutor_name', e.target.value)} data-testid="mt-tutor-name" />
        </Field>
        <Field label="Hourly rate">
          <div className="flex gap-1">
            <Input type="number" value={form.tutor_hourly_rate} onChange={(e) => set('tutor_hourly_rate', e.target.value)} data-testid="mt-tutor-rate" />
            <select value={form.tutor_hourly_rate_currency} onChange={(e) => set('tutor_hourly_rate_currency', e.target.value)}
              className="h-9 px-2 rounded border border-gray-300 text-sm">
              {['USD', 'AUD', 'KES', 'NGN', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </Field>
        <Field label="Class start time"><Input type="time" value={form.class_start_time} onChange={(e) => set('class_start_time', e.target.value)} data-testid="mt-start-time" /></Field>
        <Field label="Duration (min)"><Input type="number" value={form.session_duration_minutes} onChange={(e) => set('session_duration_minutes', e.target.value)} data-testid="mt-duration" /></Field>
        <Field label="Planned sessions"><Input type="number" value={form.planned_session_count} onChange={(e) => set('planned_session_count', e.target.value)} data-testid="mt-planned" /></Field>
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1.5">Weekdays</Label>
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map((w) => (
            <button key={w.key} type="button" onClick={() => toggleWD(w.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.selected_weekdays.includes(w.key) ? 'bg-red-700 border-red-700 text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              data-testid={`mt-weekday-${w.key}`}>{w.label}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-start">
        <Button size="sm" variant="outline" onClick={checkConflicts} disabled={busy} data-testid="mt-check-conflicts">
          <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Check conflicts
        </Button>
        {conflicts && (
          <div className="text-xs">
            {hasConflicts ? (
              <span className="text-red-700 font-semibold">
                ⚠ {(conflicts.tutor_conflicts || []).length} tutor · {(conflicts.student_conflicts || []).length} student · {(conflicts.self_conflicts || []).length} same-course conflicts
              </span>
            ) : (
              <span className="text-green-700 font-semibold">✓ No conflicts</span>
            )}
          </div>
        )}
      </div>
      {hasConflicts && (
        <Field label="Override reason (required to save with conflicts)">
          <Input value={form.conflict_override_reason} onChange={(e) => set('conflict_override_reason', e.target.value)} placeholder="e.g. Co-teaching approved by admin" data-testid="mt-override-reason" />
        </Field>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} data-testid="mt-cancel-form">Cancel</Button>
        <Button onClick={save} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="mt-save-form">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
          {module ? 'Save changes' : 'Add tutor'}
        </Button>
      </div>
    </div>
  );
};



export default AdminCourseBookingsPanel;
